'use client'
import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/outline'
import { AnimatePresence, motion } from 'framer-motion'
import { parseAsStringLiteral, useQueryState } from 'nuqs'
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  type ReactNode,
} from 'react'

import { trackEvent } from '@/lib/umami'

export const targets = [
  {
    handle: 'health-apps',
    title: 'Für Health-Apps',
  },
  {
    handle: 'studios',
    title: 'Für Studios',
  },
  {
    handle: 'ecom',
    title: 'Für eCom',
  },
] as const

export const targetHandles = targets.map((t) => t.handle)
export type TargetHandle = (typeof targetHandles)[number]

// Context for shared target state with auto-cycle control
type TargetContextValue = {
  target: TargetHandle
  displayIndex: number
  direction: number
  setTargetWithDirection: (handle: TargetHandle, direction: number) => void
}

const TargetContext = createContext<TargetContextValue | null>(null)

export function TargetProvider({ children }: { children: ReactNode }) {
  const [target, setTargetState] = useQueryState(
    't',
    parseAsStringLiteral(targetHandles).withDefault('health-apps'),
  )
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const directionRef = useRef(1)

  const displayIndex = Math.max(
    0,
    targets.findIndex((t) => t.handle === target),
  )

  const stopAutoCycle = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current)
      intervalRef.current = null
    }
  }, [])

  const setTargetWithDirection = useCallback(
    (handle: TargetHandle, direction: number) => {
      stopAutoCycle()
      directionRef.current = direction
      setTargetState(handle)
      trackEvent('audience_select', { audience: handle })
    },
    [stopAutoCycle, setTargetState],
  )

  // Auto-cycle timer
  useEffect(() => {
    let intervalCounter = 0
    intervalRef.current = setInterval(
      () => {
        intervalCounter++
        directionRef.current = 1 // Auto-cycle always goes forward
        setTargetState((prev) => {
          const prevIndex = targets.findIndex((t) => t.handle === prev)
          const nextIndex = (prevIndex + 1) % targets.length
          return targets[nextIndex].handle
        })
      },
      intervalCounter > 0 ? 3000 : 10000,
    )

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
      }
    }
  }, [setTargetState])

  return (
    <TargetContext.Provider
      value={{
        target,
        displayIndex,
        direction: directionRef.current,
        setTargetWithDirection,
      }}
    >
      {children}
    </TargetContext.Provider>
  )
}

export function useTarget() {
  const context = useContext(TargetContext)
  if (!context) {
    throw new Error('useTarget must be used within a TargetProvider')
  }
  return context
}

const tabLabels: Record<TargetHandle, string> = {
  'health-apps': 'HEALTH APPS',
  ecom: 'E-COMMERCE',
  studios: 'STUDIOS',
}

export function HorizontalTargetTabs() {
  const { target, setTargetWithDirection } = useTarget()

  return (
    <div className="flex w-full justify-center border-b border-zinc-200">
      {targets.map((t) => {
        const isActive = t.handle === target
        return (
          <button
            key={t.handle}
            onClick={() => setTargetWithDirection(t.handle, 1)}
            className="relative flex-1 px-4 py-4 text-center text-sm font-medium tracking-wide uppercase transition-colors"
          >
            <span
              className={
                isActive ? 'font-semibold text-zinc-950' : 'text-zinc-600'
              }
            >
              {tabLabels[t.handle]}
            </span>
            {isActive && (
              <motion.div
                layoutId="activeTab"
                className="absolute right-0 bottom-0 left-0 h-0.5 bg-cyan-700"
                initial={false}
                transition={{ type: 'spring', stiffness: 500, damping: 30 }}
              />
            )}
          </button>
        )
      })}
    </div>
  )
}

export function TargetSelector() {
  const { displayIndex, direction, setTargetWithDirection } = useTarget()

  const handlePrev = () => {
    const newIndex = (displayIndex - 1 + targets.length) % targets.length
    setTargetWithDirection(targets[newIndex].handle, -1)
  }

  const handleNext = () => {
    const newIndex = (displayIndex + 1) % targets.length
    setTargetWithDirection(targets[newIndex].handle, 1)
  }

  return (
    <div className="flex w-fit flex-col gap-x-2 pr-2">
      <div>
        <button onClick={handlePrev}>
          <ChevronUpIcon className="size-4 text-zinc-300 hover:text-zinc-950" />
        </button>
      </div>
      <div className="relative overflow-hidden">
        <AnimatePresence mode="wait" initial={false} custom={direction}>
          <motion.h2
            key={displayIndex}
            custom={direction}
            initial="enter"
            animate="center"
            exit="exit"
            variants={{
              enter: (dir: number) => ({ opacity: 0, y: dir * -20 }),
              center: { opacity: 1, y: 0 },
              exit: (dir: number) => ({ opacity: 0, y: dir * 20 }),
            }}
            transition={{ duration: 0.3, ease: 'easeInOut' }}
            className="font-display text-xl font-medium tracking-tight text-balance text-zinc-500 sm:text-2xl"
          >
            {targets[displayIndex].title}
          </motion.h2>
        </AnimatePresence>
      </div>
      <div>
        <button onClick={handleNext}>
          <ChevronDownIcon className="size-4 text-zinc-300 hover:text-zinc-950" />
        </button>
      </div>
    </div>
  )
}
